import {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {service as ContactService} from '../../../Services/ContactService';

/**
 * Provides project contacts to all SurveyFormInputContactRelationship inputs
 * within a survey form, loaded once rather than once per question.
 *
 * Re-fetches when:
 *   - The project changes (projectId)
 *   - The user enters edit mode (editMode true)
 *   - The browser tab becomes visible (covers "Add Contact in new tab" flow)
 *   - A consumer calls the reload function from useReloadProjectContacts
 */
const ProjectContactsContext = createContext(null);
const ProjectContactsReloadContext = createContext(() => Promise.resolve());

export const ProjectContactsProvider = ({projectId, editMode, children}) => {
    const [projectContacts, setProjectContacts] = useState(null);

    const fetchContacts = useCallback(() => {
        if (!projectId) return Promise.resolve();
        return ContactService.GetProjectContacts(projectId).then(result => {
            setProjectContacts(result?.result ?? []);
        });
    }, [projectId]);

    // Initial load and re-fetch when projectId changes
    useEffect(() => { void fetchContacts(); }, [fetchContacts]);

    // Re-fetch only when *entering* edit mode (not on exit) so that contacts
    // refreshed after save don't trigger a spurious writeAnswer in still-mounted
    // SurveyFormInputContactRelationship components, which would corrupt in-memory answers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (editMode) void fetchContacts(); }, [editMode]);

    // Re-fetch when the user returns to this tab
    useEffect(() => {
        const onVisible = () => { if (!document.hidden) void fetchContacts(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [fetchContacts]);

    return (
        <ProjectContactsReloadContext.Provider value={fetchContacts}>
            <ProjectContactsContext.Provider value={projectContacts}>
                {children}
            </ProjectContactsContext.Provider>
        </ProjectContactsReloadContext.Provider>
    );
};

export const useProjectContacts = () => useContext(ProjectContactsContext);
export const useReloadProjectContacts = () => useContext(ProjectContactsReloadContext);
